import { FormEvent, ReactNode, useEffect, useRef, useState } from "react";

import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";

import { useReturnsMutations } from "../../data/useReturns";
import { useWarehouseCheck } from "../../data/useReturnsWarehouse";
import {
  ReturnCondition,
  ReturnDetail,
} from "../../models/returns/IReturnsApi";
import classNames from "../../utils/classNames";
import {
  bandRangeText,
  conditionLabel,
  DEDUCTION_BANDS,
  refundLaterText,
  RefundNowPrefill,
  refundNowPrefill,
  showsReceivedCheck,
} from "../../utils/returnsRules";

/** The id the list's "Find" links to (`/returns/{id}#received-check`). */
export const RECEIVED_CHECK_ANCHOR = "received-check";

function errorText(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function YesNo({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation("returns");
  const base =
    "min-h-[40px] min-w-[56px] rounded-lg px-3 text-[13px] disabled:opacity-50";
  return (
    <div role="radiogroup" aria-label={label} className="flex shrink-0 gap-1">
      <button
        type="button"
        role="radio"
        aria-checked={value === true}
        disabled={disabled}
        onClick={() => onChange(true)}
        className={classNames(
          base,
          value === true
            ? "border-2 border-green-700 bg-green-50 font-semibold text-green-900"
            : "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50",
        )}
      >
        {t("Yes")}
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={value === false}
        disabled={disabled}
        onClick={() => onChange(false)}
        className={classNames(
          base,
          value === false
            ? "border-2 border-red-700 bg-red-50 font-semibold text-red-900"
            : "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50",
        )}
      >
        {t("No")}
      </button>
    </div>
  );
}

function Question({
  title,
  help,
  mono = false,
  children,
}: {
  title: string;
  help?: string | null;
  mono?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-gray-100 py-2.5 last:border-b-0">
      <span className="flex min-w-[180px] flex-1 flex-col">
        <span className="text-[13px] font-semibold text-gray-900">{title}</span>
        {help && (
          <span
            className={classNames(
              "text-[12px] text-gray-500",
              mono && "font-mono",
            )}
          >
            {help}
          </span>
        )}
      </span>
      {children}
    </div>
  );
}

interface ReceivedCheckProps {
  detail: ReturnDetail;
  /** Opened from the list's scan/find box: bring the section into view and focus it. */
  focusOnOpen: boolean;
  /** "Refund now": open the refund panel with this choice. Nothing is sent until its Confirm. */
  onRefundNow: (prefill: RefundNowPrefill) => void;
}

/**
 * "Received and checked" on the return page, desktop or phone: the checks and the condition band,
 * no photos. Saving tells eBay it's back (when eBay offers that) and then offers Refund now / later.
 * The part itself is put away in Wilma Yard as usual.
 */
export default function ReceivedCheck({
  detail,
  focusOnOpen,
  onRefundNow,
}: ReceivedCheckProps) {
  const { t } = useTranslation("returns");
  const show = showsReceivedCheck(detail);
  const context = useWarehouseCheck(show ? detail.id : undefined);
  const { check } = useReturnsMutations();
  const rootRef = useRef<HTMLElement>(null);
  const focused = useRef(false);

  const [correctPart, setCorrectPart] = useState<boolean | null>(null);
  const [partNumberMatches, setPartNumberMatches] = useState<boolean | null>(
    null,
  );
  const [engineCodeMatches, setEngineCodeMatches] = useState<boolean | null>(
    null,
  );
  const [sameCondition, setSameCondition] = useState<boolean | null>(null);
  const [difference, setDifference] = useState("");
  const [condition, setCondition] = useState<ReturnCondition | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  /** Set once this page saved the check, so Refund now / later shows in place of the form. */
  const [checked, setChecked] = useState<ReturnCondition | null>(null);
  const [choice, setChoice] = useState<"now" | "later" | null>(null);
  const [ebayNote, setEbayNote] = useState<string>("");

  useEffect(() => {
    if (!focusOnOpen || focused.current || !show || !rootRef.current) return;
    focused.current = true;
    rootRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    rootRef.current.focus({ preventScroll: true });
  }, [focusOnOpen, show]);

  if (!show && !checked) return null;

  const c = context.data;
  const engineApplies = !!c?.engineCodeApplies;
  const busy = check.isLoading;
  const ready =
    !!c &&
    correctPart !== null &&
    partNumberMatches !== null &&
    (!engineApplies || engineCodeMatches !== null) &&
    sameCondition !== null &&
    (sameCondition || difference.trim().length > 0) &&
    condition !== null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (
      !ready ||
      correctPart === null ||
      partNumberMatches === null ||
      sameCondition === null ||
      condition === null
    )
      return;
    setError(null);
    try {
      const saved = await check.mutateAsync({
        id: detail.id,
        body: {
          correctPart,
          partNumberMatches,
          engineCodeMatches: engineApplies ? engineCodeMatches : null,
          sameCondition,
          difference: sameCondition ? null : difference.trim(),
          condition,
          note: note.trim() || null,
        },
      });
      setEbayNote(saved.message);
      setChecked(condition);
    } catch (err) {
      setError(errorText(err, t("Couldn't save the check.")));
    }
  };

  const refundDueBy =
    detail.deadlines?.refundDueBy ??
    (detail.deadline.kind === "refund" ? detail.deadline.at : null);

  if (checked) {
    const itemised =
      detail.estimatedRefund?.item !== null &&
      detail.estimatedRefund?.item !== undefined;
    const prefill = refundNowPrefill(checked, detail.allowedActions, itemised);
    return (
      <section
        id={RECEIVED_CHECK_ANCHOR}
        ref={rootRef}
        tabIndex={-1}
        aria-label={t("Received and checked")}
        className="flex flex-col gap-2.5 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-[13px] text-green-900 focus:outline-none"
      >
        <span className="flex items-center gap-2 font-semibold">
          <CheckCircleIcon className="h-5 w-5 text-green-700" />
          {t("Received and checked · {{condition}}", {
            condition: conditionLabel(checked),
          })}
        </span>
        {ebayNote && <span className="text-[12px]">{ebayNote}</span>}
        {choice === "now" ? (
          <span className="text-[12px]">
            {t(
              "The refund is open in What eBay allows now. Nothing is sent until you confirm it there.",
            )}
          </span>
        ) : choice === "later" || !prefill ? (
          <span className="text-[12px]">
            {!prefill && choice !== "later" && detail.channel === "ebay"
              ? `${t("eBay doesn't offer the refund on this return yet.")} `
              : ""}
            {refundLaterText(refundDueBy, detail.channel)}
          </span>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setChoice("now");
                onRefundNow(prefill);
              }}
              className="min-h-[40px] rounded-lg bg-primary-600 px-3.5 text-[13px] font-semibold text-white hover:bg-primary-700"
            >
              {t("Refund now")}
            </button>
            <button
              type="button"
              onClick={() => setChoice("later")}
              className="min-h-[40px] rounded-lg border border-gray-300 bg-white px-3.5 text-[13px] text-gray-800 hover:bg-gray-50"
            >
              {t("Refund later")}
            </button>
            <span className="text-[12px] text-green-800">
              {prefill.kind === "refund_full"
                ? t("Opens a full refund.")
                : t(
                    "Opens a refund with a {{pct}}% deduction ({{condition}} band) — you can still choose a full refund.",
                    {
                      pct: prefill.deductionPct,
                      condition: conditionLabel(prefill.condition),
                    },
                  )}
            </span>
          </div>
        )}
      </section>
    );
  }

  return (
    <section
      id={RECEIVED_CHECK_ANCHOR}
      ref={rootRef}
      tabIndex={-1}
      aria-label={t("Received and checked")}
      className="scroll-mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
        <h2 className="text-[13px] font-semibold text-gray-900">
          {t("Received and checked")}
        </h2>
        <span className="text-[11px] text-gray-500">
          {t(
            "Fill this in when the part is back in your hands. Put it away in Wilma Yard as usual.",
          )}
        </span>
      </div>
      <form onSubmit={submit} className="flex flex-col gap-3 px-4 py-3">
        {context.isError && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-[12px] text-red-800">
            {errorText(
              context.error,
              t("Couldn't load the part details for the check."),
            )}{" "}
            <button
              type="button"
              onClick={() => context.refetch()}
              className="font-semibold underline"
            >
              {t("Try again")}
            </button>
          </p>
        )}
        <div className="flex flex-col">
          <Question
            title={t("Correct part?")}
            help={
              [
                c?.partTitle ?? detail.part?.title,
                c?.tagNumber ?? detail.part?.tagNumber,
              ]
                .filter(Boolean)
                .join(" · ") || null
            }
          >
            <YesNo
              label={t("Correct part?")}
              value={correctPart}
              onChange={setCorrectPart}
              disabled={busy}
            />
          </Question>
          <Question
            title={t("Part number matches?")}
            mono
            help={
              c
                ? c.partNumber || t("No part number on the part")
                : t("Loading…")
            }
          >
            <YesNo
              label={t("Part number matches?")}
              value={partNumberMatches}
              onChange={setPartNumberMatches}
              disabled={busy}
            />
          </Question>
          {engineApplies && (
            <Question
              title={t("Engine code matches?")}
              mono
              help={c?.engineCode || t("No engine code on the part")}
            >
              <YesNo
                label={t("Engine code matches?")}
                value={engineCodeMatches}
                onChange={setEngineCodeMatches}
                disabled={busy}
              />
            </Question>
          )}
          <Question title={t("Same condition as sent?")}>
            <YesNo
              label={t("Same condition as sent?")}
              value={sameCondition}
              onChange={setSameCondition}
              disabled={busy}
            />
          </Question>
          {sameCondition === false && (
            <label className="flex flex-col gap-1 pb-2.5 text-[12px] font-semibold text-gray-700">
              {t("What's different?")}
              <input
                value={difference}
                onChange={(e) => setDifference(e.target.value)}
                required
                maxLength={500}
                disabled={busy}
                placeholder={t("e.g. cracked lens, missing bracket")}
                className="rounded-lg border border-gray-300 px-3 py-2 text-[13px] font-normal text-gray-900 focus:border-primary-500 focus:ring-primary-500"
              />
            </label>
          )}
        </div>

        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1 text-[13px] font-semibold text-gray-900">
            {t("Condition")}
          </legend>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {DEDUCTION_BANDS.map((b) => {
              const active = condition === b.condition;
              return (
                <label
                  key={b.condition}
                  className={classNames(
                    "flex min-h-[40px] cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[13px]",
                    active
                      ? "border-primary-600 bg-primary-50"
                      : "border-gray-200 hover:bg-gray-50",
                  )}
                >
                  <input
                    type="radio"
                    name="received-condition"
                    checked={active}
                    onChange={() => setCondition(b.condition)}
                    disabled={busy}
                    className="border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span
                    className={classNames(
                      "flex-1",
                      active ? "font-semibold text-gray-900" : "text-gray-800",
                    )}
                  >
                    {conditionLabel(b.condition)}
                  </span>
                  <span className="text-[12px] text-gray-500">
                    {b.max === 0
                      ? t("no deduction")
                      : t("{{range}} deduction", { range: bandRangeText(b) })}
                  </span>
                </label>
              );
            })}
          </div>
          <span className="text-[11px] text-gray-500">
            {t("eBay's bands: a % of the item price, never postage.")}
          </span>
        </fieldset>

        <label className="flex flex-col gap-1 text-[12px] font-semibold text-gray-700">
          <span>
            {t("Note")}{" "}
            <span className="font-normal text-gray-500">
              · {t("optional, for the team")}
            </span>
          </span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={1000}
            disabled={busy}
            className="resize-y rounded-lg border border-gray-300 px-3 py-2 text-[13px] font-normal text-gray-900 focus:border-primary-500 focus:ring-primary-500"
          />
        </label>

        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-800">
            {error}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="submit"
            disabled={busy || !ready}
            className="min-h-[44px] rounded-lg bg-primary-600 px-4 text-[13px] font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? t("Saving…") : t("Received and checked")}
          </button>
          <span className="text-[11px] text-gray-500">
            {detail.channel === "ebay"
              ? t(
                  "Saves the check and tells eBay it's back, if eBay is waiting for that. No refund is sent.",
                )
              : t("Saves the check on the return. No refund is sent.")}
          </span>
        </div>
      </form>
    </section>
  );
}
